import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { reportsApi, type ReportRow } from '../../api/reports.js';
import { EmptyBox, ErrorBox, Pagination } from '../../components/ui.js';
import { datetime, num, stripImgTags } from '../../lib/format.js';
import { useUrlSearchFields, useUrlUpdate } from '../../lib/hooks.js';

const REPORT_ID = 'item-orders-log';

type ActionKind = 'add' | 'del' | 'chg';

const ACTIONS: Record<ActionKind, { label: string; description: string }> = {
  add: { label: '추가', description: '진열 목록에 추가됨' },
  del: { label: '삭제', description: '진열 목록에서 삭제됨' },
  chg: { label: '변경', description: '진열 순서가 변경됨' },
};

function Icon({ name }: { name: 'activity' | 'download' | 'refresh' | 'search' | 'sliders' | 'x' }) {
  const path = {
    activity: <><path d="M3 12h3l2.2-6 3.5 12 2.2-6H21" /></>,
    download: <><path d="M12 3v11m0 0 4-4m-4 4-4-4" /><path d="M5 19h14" /></>,
    refresh: <><path d="M20 7h-5V2" /><path d="M19 7a8 8 0 1 0 1 7" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    sliders: <><path d="M4 7h10M18 7h2M4 17h2m4 0h10" /><circle cx="16" cy="7" r="2" /><circle cx="8" cy="17" r="2" /></>,
    x: <><path d="m7 7 10 10M17 7 7 17" /></>,
  }[name];

  return <svg className="item-log-icon" viewBox="0 0 24 24" aria-hidden="true">{path}</svg>;
}

function actionKind(row: ReportRow): ActionKind | undefined {
  const value = String(row.add_type ?? '');
  return value === 'add' || value === 'del' || value === 'chg' ? value : undefined;
}

function ActionBadge({ row, compact = false }: { row: ReportRow; compact?: boolean }) {
  const kind = actionKind(row);
  if (!kind) return <span className="item-log-action item-log-action--unknown">{String(row.add_type ?? '-')}</span>;

  return (
    <span className={`item-log-action item-log-action--${kind}${compact ? ' item-log-action--compact' : ''}`}>
      <span className="item-log-action__icon" aria-hidden="true">{kind === 'add' ? '+' : kind === 'del' ? '−' : '↕'}</span>
      <span>{ACTIONS[kind].label}</span>
    </span>
  );
}

function productTypeLabel(value: unknown) {
  return value === 'best' ? '추천상품' : value === 'list' ? '일반상품' : String(value ?? '-');
}

function pageLabel(value: unknown) {
  return value === 'orders' ? '분류진열관리 · 전체' : cleanText(value);
}

function dateParts(value: unknown): { date: string; time: string } {
  const formatted = datetime(String(value ?? ''));
  if (formatted === '-') return { date: '-', time: '' };
  const [date = formatted, time = ''] = formatted.split(' ');
  return { date, time };
}

function cleanText(value: unknown, fallback = '-') {
  const cleaned = stripImgTags(value).replace(/<[^>]*>/g, '').trim();
  return cleaned || fallback;
}

function ResultSkeleton() {
  return (
    <div className="item-log-skeleton" role="status" aria-label="변경 이력을 불러오는 중">
      <span className="sr-only">변경 이력을 불러오는 중…</span>
      {Array.from({ length: 7 }, (_, index) => <i key={index} />)}
    </div>
  );
}

function DesktopTable({ rows }: { rows: ReportRow[] }) {
  return (
    <div className="item-log-table-wrap">
      <table className="item-log-table">
        <caption className="sr-only">상품 분류 진열 순서 변경 이력</caption>
        <thead>
          <tr>
            <th>작업</th>
            <th>상품</th>
            <th>분류 및 진열</th>
            <th>작업자</th>
            <th>일시</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const when = dateParts(row.regdate);
            const kind = actionKind(row);
            return (
              <tr key={String(row.index_no ?? index)}>
                <td>
                  <ActionBadge row={row} />
                  <span className="item-log-action-desc">{kind ? ACTIONS[kind].description : '작업 유형 미확인'}</span>
                </td>
                <td>
                  <strong className="item-log-product-name" title={cleanText(row.gname)}>{cleanText(row.gname)}</strong>
                  <span className="item-log-meta">상품번호 {cleanText(row.goods_idx)}</span>
                </td>
                <td>
                  <strong className="item-log-category">{cleanText(row.catename)}</strong>
                  <span className="item-log-meta">
                    {productTypeLabel(row.goods_type)} <i aria-hidden="true" /> 정렬 {num(row.orders as string | number | null)}
                  </span>
                </td>
                <td>
                  <strong>{cleanText(row.adminname, '알 수 없음')}</strong>
                  <span className="item-log-meta">{pageLabel(row.page_code)}</span>
                </td>
                <td className="item-log-time">
                  <strong>{when.date}</strong>
                  <span>{when.time}</span>
                  <small>로그 #{cleanText(row.index_no)}</small>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MobileCards({ rows }: { rows: ReportRow[] }) {
  return (
    <ol className="item-log-cards" aria-label="상품 변경 이력">
      {rows.map((row, index) => {
        const when = dateParts(row.regdate);
        return (
          <li key={String(row.index_no ?? index)} className="item-log-card">
            <div className="item-log-card__top">
              <ActionBadge row={row} compact />
              <time dateTime={String(row.regdate ?? '')}>{when.date} <span>{when.time}</span></time>
            </div>
            <strong className="item-log-card__product">{cleanText(row.gname)}</strong>
            <span className="item-log-card__code">상품번호 {cleanText(row.goods_idx)}</span>
            <dl>
              <div><dt>카테고리</dt><dd>{cleanText(row.catename)}</dd></div>
              <div><dt>진열 정보</dt><dd>{productTypeLabel(row.goods_type)} · 정렬 {num(row.orders as string | number | null)}</dd></div>
              <div><dt>작업자</dt><dd>{cleanText(row.adminname, '알 수 없음')}</dd></div>
            </dl>
          </li>
        );
      })}
    </ol>
  );
}

export function ItemOrdersLogPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [searchValues, setSearchValue] = useUrlSearchFields(params, update, ['q', 'goodsIdx'], 300);
  const query = searchValues.q ?? '';
  const goodsIdx = searchValues.goodsIdx ?? '';

  const queryParams = useMemo(() => {
    const next: Record<string, string | number> = {};
    for (const [key, value] of params.entries()) if (value !== '') next[key] = value;
    return next;
  }, [params]);

  const meta = useQuery({
    queryKey: ['report', REPORT_ID, 'meta'],
    queryFn: () => reportsApi.meta(REPORT_ID),
    staleTime: 5 * 60 * 1000,
  });
  const data = useQuery({
    queryKey: ['report', REPORT_ID, queryParams],
    queryFn: () => reportsApi.data(REPORT_ID, queryParams),
    placeholderData: keepPreviousData,
  });

  const activeFilterCount = ['q', 'goodsIdx', 'goodsType', 'addType'].filter((key) => params.get(key)).length;
  const currentAction = params.get('addType') ?? '';
  const rows = data.data?.items ?? [];
  const pageCounts = rows.reduce<Record<ActionKind, number>>((counts, row) => {
    const kind = actionKind(row);
    if (kind) counts[kind] += 1;
    return counts;
  }, { add: 0, del: 0, chg: 0 });
  const latestRow = rows.reduce<ReportRow | undefined>((latest, row) => {
    if (!latest) return row;
    return String(row.regdate ?? '') > String(latest.regdate ?? '') ? row : latest;
  }, undefined);
  const latest = latestRow ? dateParts(latestRow.regdate) : null;

  function clearFilters() {
    setParams({}, { replace: true });
  }

  return (
    <section className="page--full item-log-page">
      <header className="item-log-hero">
        <div className="item-log-hero__copy">
          <span className="item-log-eyebrow"><Icon name="activity" /> CATALOG ACTIVITY</span>
          <h1>상품 정렬 변경 이력</h1>
          <p>카테고리 진열 순서의 추가·삭제·변경 내역을 빠르게 추적합니다.</p>
        </div>
        <div className="item-log-hero__actions">
          {data.isFetching && !data.isPending && <span className="item-log-sync" role="status" aria-live="polite"><i /> 업데이트 중</span>}
          <button className="item-log-button item-log-button--secondary" type="button" onClick={() => void data.refetch()} disabled={data.isFetching}>
            <Icon name="refresh" /> 새로고침
          </button>
          <a className="item-log-button item-log-button--primary" href={reportsApi.exportUrl(REPORT_ID, queryParams)}>
            <Icon name="download" /> CSV 내보내기
          </a>
        </div>
      </header>

      <div className="item-log-overview" aria-label="변경 이력 요약">
        <div className="item-log-overview__total">
          <span>검색 결과</span>
          <strong>{data.data ? data.data.total.toLocaleString() : '—'}<small>건</small></strong>
        </div>
        <div className="item-log-overview__item item-log-overview__item--add"><i aria-hidden="true">+</i><span>현재 페이지 추가<strong>{pageCounts.add.toLocaleString()}</strong></span></div>
        <div className="item-log-overview__item item-log-overview__item--del"><i aria-hidden="true">−</i><span>현재 페이지 삭제<strong>{pageCounts.del.toLocaleString()}</strong></span></div>
        <div className="item-log-overview__item item-log-overview__item--chg"><i aria-hidden="true">↕</i><span>현재 페이지 변경<strong>{pageCounts.chg.toLocaleString()}</strong></span></div>
        <div className="item-log-overview__latest">
          <span>현재 목록의 최근 기록</span>
          <strong>{latest ? `${latest.date} ${latest.time}` : '—'}</strong>
        </div>
      </div>

      <form className="item-log-filters" role="search" onSubmit={(event) => event.preventDefault()}>
        <div className="item-log-filter-head">
          <span><Icon name="sliders" /> 필터</span>
          {activeFilterCount > 0 && (
            <button type="button" onClick={clearFilters}><Icon name="x" /> 조건 초기화 <b>{activeFilterCount}</b></button>
          )}
        </div>
        <div className="item-log-filter-grid">
          <label className="item-log-search">
            <span>상품명 검색</span>
            <div><Icon name="search" /><input type="search" value={query} onChange={(event) => setSearchValue('q', event.target.value)} placeholder="상품명을 입력하세요" /></div>
          </label>
          <label className="item-log-code-search">
            <span>상품번호</span>
            <input inputMode="numeric" value={goodsIdx} onChange={(event) => setSearchValue('goodsIdx', event.target.value)} placeholder="예: 123456" />
          </label>
          <label>
            <span>상품 유형</span>
            <select value={params.get('goodsType') ?? ''} onChange={(event) => update({ goodsType: event.target.value })}>
              <option value="">전체 상품</option>
              <option value="list">일반상품</option>
              <option value="best">추천상품</option>
            </select>
          </label>
          <label>
            <span>정렬 기준</span>
            <select value={(params.get('dir') ?? 'DESC').toUpperCase()} onChange={(event) => update({ sort: 'index_no', dir: event.target.value }, false)}>
              <option value="DESC">최신순</option>
              <option value="ASC">오래된순</option>
            </select>
          </label>
        </div>
        <fieldset className="item-log-segments">
          <legend>작업 유형</legend>
          <div>
            {[
              ['', '전체'],
              ['add', '추가'],
              ['del', '삭제'],
              ['chg', '변경'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={currentAction === value ? 'is-active' : undefined}
                aria-pressed={currentAction === value}
                onClick={() => update({ addType: value })}
              >
                {value && <i className={`item-log-segment-dot item-log-segment-dot--${value}`} aria-hidden="true" />}
                {label}
              </button>
            ))}
          </div>
        </fieldset>
      </form>

      <div className="item-log-results-head">
        <div>
          <h2>변경 활동</h2>
          <p>{activeFilterCount > 0 ? '선택한 조건에 맞는 기록입니다.' : '최근 변경부터 표시됩니다.'}</p>
        </div>
        {data.data && <span>{data.data.page} / {Math.max(data.data.totalPages, 1)} 페이지</span>}
      </div>

      {data.isPending ? (
        <ResultSkeleton />
      ) : data.isError ? (
        <ErrorBox error={data.error} onRetry={() => void data.refetch()} />
      ) : rows.length === 0 ? (
        <div className="item-log-empty">
          <EmptyBox>조건에 맞는 변경 이력이 없습니다.</EmptyBox>
          {activeFilterCount > 0 && <button type="button" onClick={clearFilters}>모든 조건 지우기</button>}
        </div>
      ) : (
        <>
          <DesktopTable rows={rows} />
          <MobileCards rows={rows} />
          <Pagination
            page={data.data.page}
            totalPages={data.data.totalPages}
            total={data.data.total}
            onChange={(page) => update({ page }, false)}
          />
          {meta.data?.legacyPath && <p className="item-log-source">데이터 원본 <code>{meta.data.legacyPath}</code></p>}
        </>
      )}
    </section>
  );
}
